import { Action } from 'com/gongxigames/math/core/game/enums/Action';
import { PromotionDecision, ResponseBody } from '@gongxigames/framework/integrations/rubyplay/types';
import { RubyPlayGameClientWrapper } from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapper';
import { RubyPlayGameClientWrapperSettings } from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';
import { dateDiffInSeconds } from '@gongxigames/framework/slot';
import {
    IMultiSetsPromotionInterface,
    PromotionTypes,
    IPromotionalStatusesInterface,
    PromotionBalanceEntry,
    PromotionBalanceEntryQualifier,
    PromotionBalanceExtractor,
    MultiSetsPromotionInitData,
} from '@gongxigames/framework/promotions';

const PAID_ROUND_FIELD = { paid_round: 1 };

export class MultiSetsPromotion implements IMultiSetsPromotionInterface, IPromotionalStatusesInterface {
    public static PING_BALANCE_DELAY = 60000;
    protected sets: PromotionBalanceEntry[] = [];
    protected currentSet?: PromotionBalanceEntry;
    private expiredSet?: PromotionBalanceEntry;
    private activePromotionId?: string;
    private clientWrapper!: RubyPlayGameClientWrapper;
    private defaultBet = -1;

    public getType(): PromotionTypes {
        return PromotionTypes.MultiSetsPromotion;
    }

    public init(data: MultiSetsPromotionInitData): void {
        const { initResponse, clientWrapper, defaultBet } = data;
        const { player, active_promotion_id } = initResponse.data;
        const promotionBalance = PromotionBalanceExtractor.extractFromPlayer(player);

        this.clientWrapper = clientWrapper;
        this.defaultBet = defaultBet;
        this.sets = this.getActivePromotion(promotionBalance);

        if (this.sets.length > 0) {
            if (this.currentSet === undefined) {
                this.currentSet = this.sets[0];
            }

            if (this.activePromotionId === undefined && active_promotion_id === this.currentSet.id) {
                this.activePromotionId = this.currentSet.id;
            }
        } else if (promotionBalance.length === 1) {
            // we can set this status only for case when we have one promotion, so it was expired
            // (for 2 and more no need to do this)
            const promotionSet = promotionBalance[0];

            if (promotionSet.remained === 0) {
                this.expiredSet = promotionSet;
            }
        }
    }

    public update(response: ResponseBody): void {
        const { player, active_promotion_id } = response.data;

        this.sets = PromotionBalanceExtractor.extractFromPlayer(player);

        if (active_promotion_id) {
            this.activePromotionId = active_promotion_id;
        }

        const updatedSet = this.sets.find(({ id }) => this.currentSet?.id === id);

        if (this.currentSet && updatedSet) {
            this.currentSet = updatedSet;
        }
    }

    public getQualifier(): PromotionBalanceEntryQualifier {
        return this.getCurrentSet().type;
    }

    public hasPromotion(): boolean {
        return this.sets.length > 0;
    }

    public getPlayedRounds(): number {
        return this.getCurrentSet().awarded - this.getCurrentSet().remained;
    }

    public getRemainedRounds(): number {
        return this.getCurrentSet().remained;
    }

    public getTotalRounds(): number {
        return this.getCurrentSet().awarded;
    }

    public isActive(): boolean {
        const set = this.currentSet;

        return set !== undefined && set.id === this.activePromotionId;
    }

    public isCurrentInUse(): boolean {
        return this.currentSet !== undefined && this.isInUse(this.currentSet);
    }

    public isCurrentExpired(): boolean {
        const set = this.currentSet;

        return set !== undefined && set.id === this.activePromotionId && this.isExpired(set);
    }

    public subtractPromotion(): void {
        this.getCurrentSet().remained -= 1;
    }

    private getCurrentSet(): PromotionBalanceEntry {
        if (this.currentSet) {
            return this.currentSet;
        }

        throw new Error('No active free rounds set');
    }

    public end(): void {
        this.sets = this.getNextAvailableSets();
        this.activePromotionId = undefined;
        this.currentSet = undefined;
        this.clientWrapper.updateDefaultBet();

        if (this.sets.length > 0) {
            this.currentSet = this.sets[0];
        }
    }

    protected getNextAvailableSets(): PromotionBalanceEntry[] {
        return this.getActivePromotion(this.sets).filter(({ id }) => id !== this.currentSet?.id);
    }

    public setChoice(choice: PromotionDecision, callback: () => void): void {
        this.clientWrapper.sendPromotionDecision(
            choice,
            () => {
                if (choice === 'accept') {
                    this.activePromotionId = this.getCurrentSet().id;
                    this.clientWrapper.updateDefaultBet();
                }

                callback();
            },
            () => {}
        );
    }

    public getBet(): number {
        const subunit = this.clientWrapper.getSettings<RubyPlayGameClientWrapperSettings>().get('subunit');
        const wager = this.clientWrapper.wager;
        const bet = this.getCurrentSet().bet;

        if (!bet) return this.defaultBet;

        // Convert to subunits and round to nearest integer
        const betInSubunits = Math.round(bet * subunit);

        // Divide by wager if needed (depending on game math)
        const normalized = betInSubunits / wager;

        return normalized;
    }

    public getID(): string {
        return this.getCurrentSet().id;
    }

    public getWin(): number {
        const subunit = this.clientWrapper.getSettings<RubyPlayGameClientWrapperSettings>().get('subunit');

        return (this.getCurrentSet().collectedWin || 0) * subunit;
    }

    public getAdditionalFieldsForServerRequest(action: Action): Record<string, any> | undefined {
        // in case when FRs expired we should add additional field to request to server on first paid spin
        if (this.expiredSet && action === Action.SPIN) {
            this.expiredSet = undefined;

            return PAID_ROUND_FIELD;
        }

        return undefined;
    }

    public getPingBalanceDelay(): number {
        return MultiSetsPromotion.PING_BALANCE_DELAY;
    }

    protected getActivePromotion(data: PromotionBalanceEntry[]): PromotionBalanceEntry[] {
        return data.filter((promotion) => {
            if (this.isQueued(promotion) || this.isInUse(promotion)) {
                return promotion;
            }
        });
    }

    public isQueued(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'QUEUED';
    }

    public isInUse(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'IN_USE' || (balance.remained > 0 && balance.remained < balance.awarded);
    }

    public isCompleted(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'COMPLETED' || balance.remained === 0;
    }

    public isExpired(balance: PromotionBalanceEntry): boolean {
        const isExpired = dateDiffInSeconds(Date.now(), new Date(`${balance.expirationTime}Z`).getTime()) < 0;

        return balance.status === 'EXPIRED' || isExpired;
    }

    public isOptOut(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'OPT_OUT';
    }

    public isDisabled(balance: PromotionBalanceEntry): boolean {
        return balance.status === 'DISABLED';
    }
}
